import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { setToken, isAuthenticated } from '../utils/auth';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const routeUser = (user) => {
      if (!user.is_profile_complete) {
        navigate('/profile-setup', { state: { user } });
      } else if (user.matches_remaining > 0) {
        navigate('/dashboard', { state: { user } });
      } else {
        navigate('/paywall', { state: { user } });
      }
    };

    const processAuth = async () => {
      // The session_id normally arrives in the hash fragment; some browsers/webviews
      // move it to the query string, so accept both.
      const sessionIdMatch = (location.hash || '').match(/session_id=([^&]+)/) ||
        (location.search || '').match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        toast.error('Authentication failed - no session found');
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      // A session_id can only be exchanged ONCE. If this page is reloaded or restored
      // from the back/forward cache, reuse the login we already completed.
      if (sessionStorage.getItem('oauth_session_used') === sessionId && isAuthenticated()) {
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard');
        return;
      }

      try {
        const response = await api.post('/auth/google/callback', { session_id: sessionId });

        if (!response.data.token) throw new Error('No token received');

        sessionStorage.setItem('oauth_session_used', sessionId);
        setToken(response.data.token);
        window.history.replaceState(null, '', window.location.pathname);

        toast.success(`Welcome${response.data.name ? `, ${response.data.name.split(' ')[0]}` : ''}!`);
        routeUser(response.data);
      } catch (error) {
        console.error('Google auth error:', error);
        // The exchange failed but an earlier one in this tab already signed us in
        if (isAuthenticated()) {
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard');
          return;
        }
        const errorMsg = error.response?.data?.detail || 'Google sign-in failed. Please try again.';
        toast.error(errorMsg);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, location.hash, location.search]);

  return (
    <div className="app-container gradient-bg" data-testid="auth-callback-screen">
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="animate-spin text-primary mb-4" size={48} />
        <p className="text-white text-lg">Completing sign in...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we set up your account</p>
      </div>
    </div>
  );
}
